import WolfTooltip from '@components/ui/WolfTooltip';
import { useTranslation } from '@hooks/useTranslation';
import { DeleteOutline, PlayArrow } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import React, { useState } from 'react';

interface PendingsListProps {
  pendings: any[];
  handleCompletePending: (pendingId: string) => void;
  handleContinuePending: (pendingId: string) => void;
  loadingPendingId?: string | null;
}

const PendingsList: React.FC<PendingsListProps> = ({ pendings, handleCompletePending, handleContinuePending, loadingPendingId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const localeDiscrepancies = useTranslation('discrepancies');

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionTypeText = (type: string) => {
    switch (type) {
      case 'PROCESS_CREATION':
        return 'Process Creation';
      case 'STEP_CREATION':
        return 'Step Creation';
      case 'POLICY_LINKING':
        return 'Policy Linking';
      default:
        return type;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const paginatedPendings = pendings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (pendings.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center py-8">
        <Typography variant="body1" className="text-gray-600">
          No pending actions
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="w-full">
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{localeDiscrepancies['pendingType']}</TableCell>
              <TableCell>{localeDiscrepancies['pendingName']}</TableCell>
              <TableCell>{localeDiscrepancies['pendingStatus']}</TableCell>
              <TableCell>{localeDiscrepancies['pendingDate']}</TableCell>
              <TableCell align="right">{localeDiscrepancies['pendingActions']}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPendings.map((pending: any, index: number) => {
              const pendingName = pending.metadata?.name || pending.context?.name || 'N/A';
              const isLoading = loadingPendingId === pending.pendingId;
              return (
                <TableRow key={pending.pendingId || index} hover>
                  <TableCell>{getActionTypeText(pending.type)}</TableCell>
                  <TableCell>{pendingName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{getStatusText(pending.status)}</TableCell>
                  <TableCell>
                    {pending.createdAt
                      ? new Date(pending.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    {isLoading ? (
                      <Box className="flex gap-2 justify-end items-center">
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box className="flex gap-2 justify-end">
                        <WolfTooltip title={localeDiscrepancies['continuePendingTooltip']} arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleContinuePending(pending.pendingId)}
                            color="primary">
                            <PlayArrow />
                          </IconButton>
                        </WolfTooltip>
                        <WolfTooltip title={localeDiscrepancies['completePendingTooltip']} arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleCompletePending(pending.pendingId)}>
                            <DeleteOutline />
                          </IconButton>
                        </WolfTooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {pendings.length > 10 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={pendings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default PendingsList;
